import React, { useState } from "react";
import { BrandMark } from "./BrandMark";
import { NavLink, Link, useLocation } from "react-router-dom";
import { FiActivity, FiCompass, FiDatabase, FiFileText, FiGrid, FiMenu, FiMoon, FiSun, FiUser, FiX } from "react-icons/fi";
import { useAuth } from "../context/AuthContext.tsx";
import { useHandleTheme } from "../context/ThemeContext";

const links = [
  { to: "/", label: "Overview", icon: FiGrid, end: true },
  { to: "/connections", label: "Connect", icon: FiDatabase },
  { to: "/evidence", label: "Proof", icon: FiFileText },
  { to: "/marketplace", label: "Marketplace", icon: FiCompass },
  { to: "/developers", label: "Developers", icon: FiActivity },
];

const Header: React.FC = () => {
  const { isAuthenticated, loginWithGoogle, user } = useAuth();
  const { theme, toggleTheme } = useHandleTheme();
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  React.useEffect(() => { setMenuOpen(false); }, [location.pathname]);
  React.useEffect(() => {
    if (!menuOpen) return;
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("keydown", onEscape);
    return () => document.removeEventListener("keydown", onEscape);
  }, [menuOpen]);

  const renderLinks = (mobile = false) => links.map(item => {
    const Icon = item.icon;
    return (
      <NavLink key={item.to} to={item.to} end={item.end}
        className={({ isActive }) => "handle-nav-link" + (isActive ? " is-active" : "")}
        onClick={mobile ? () => setMenuOpen(false) : undefined}>
        <Icon size={17} aria-hidden="true" />
        <span>{item.label}</span>
      </NavLink>
    );
  });

  return (
    <>
      <header className="handle-header">
        <div className="handle-header-inner">
          <Link to="/" className="handle-wordmark" aria-label="Handle overview">
            <BrandMark className="handle-brand-symbol" decorative />
            <span className="handle-brand-name">handle<span className="handle-brand-dot">.</span></span>
            <span className="handle-brand-label">DATA OS</span>
          </Link>
          <nav className="handle-header-nav" aria-label="Main navigation">{renderLinks()}</nav>
          <div className="handle-header-actions">
            <span className="handle-network-indicator"><span /> Base Sepolia</span>
            <button type="button" className="handle-icon-button" onClick={toggleTheme}
              aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
              title={theme === "dark" ? "Light mode" : "Dark mode"}>
              {theme === "dark" ? <FiSun size={19} /> : <FiMoon size={19} />}
            </button>
            {isAuthenticated ? (
              <Link to="/profile" className="handle-account-button" aria-label="Your profile">
                <FiUser size={17}/><span>{user?.firstName || "Account"}</span>
              </Link>
            ) : (
              <button type="button" className="handle-account-button" onClick={loginWithGoogle}>Sign in <span className="handle-login-arrow">↗</span></button>
            )}
            <button type="button" className="handle-icon-button handle-mobile-menu" aria-label={menuOpen ? "Close menu" : "Open menu"}
              aria-expanded={menuOpen} aria-controls="handle-mobile-drawer" onClick={() => setMenuOpen(!menuOpen)}>
              {menuOpen ? <FiX size={21} /> : <FiMenu size={21} />}
            </button>
          </div>
        </div>
        {menuOpen && (
          <div id="handle-mobile-drawer" className="handle-mobile-drawer">
            <nav aria-label="Mobile navigation">{renderLinks(true)}</nav>
            <div className="handle-mobile-drawer-extras">
              <Link to="/profile" onClick={() => setMenuOpen(false)}><FiUser size={17}/> Account & settings</Link>
              <Link to="/trust" onClick={() => setMenuOpen(false)}><FiFileText size={17}/> Trust & verification</Link>
            </div>
          </div>
        )}
      </header>
      <nav className="handle-mobile-tabs" aria-label="Mobile primary navigation">{renderLinks(true)}</nav>
    </>
  );
};

export default Header;
